import React from "react";
import { ReactNode } from "react";
import ObservableState from "../utils/ObservableState";
import * as ui from "../native";
import ObservableComponent from "./ObservableComponent";
import BaseUIProps, { copyBaseUIProps } from "../native/ui/BaseUIProps";
import ObjectObservable from "../utils/ObjectObservable";
import ResultStatus from "../classes/ResultStatus";
import LabelWithInputField from "./LabelWithInputField";
import SuccessMessage from "../classes/SuccessMessage";
import EventBus from "../utils/EventBus";
import PageNavigator from "../classes/PageNavigator";
import UserType from "../classes/UserType";
import Result from "../classes/Result";
import BooleanToggleView from "./BooleanToggleView";
import NOKUser from "../models/NOKUser";
import PopupWrapperView from "./PopupWrapperView";
import Button from "./Button";
import DBSaveStatus from "../utils/DBSaveStatus";
import ListWrapper from "../utils/ListWrapper";
import TextView from "./TextView";
import Hospital from "../models/Hospital";
import LabelDropdown from "./LabelDropdown";
import RPCServices from "../rpc/RPCServices";
import CollectionUtils from "../utils/CollectionUtils";
import { Runnable } from "../classes/core";
import { BuildContext } from "../classes/BuildContext";

type _CancelButtonOnPressed = (d3eState: AddOrEditSupportUserViewRefs) => void;

type _SaveButtonOnPressed = (d3eState: AddOrEditSupportUserViewRefs) => void;

type _LabelwithInputFieldOnChanged = (
  text: string,
  d3eState: AddOrEditSupportUserViewRefs
) => void;

type _LabelwithInputField2OnChanged = (
  text: string,
  d3eState: AddOrEditSupportUserViewRefs
) => void;

type _LabelwithInputField8OnChanged = (
  text: string,
  d3eState: AddOrEditSupportUserViewRefs
) => void;

type _LabelDropdown1OnChanged = (
  text: UserType,
  d3eState: AddOrEditSupportUserViewRefs
) => void;

type _LabelwithInputField3OnChanged = (
  text: string,
  d3eState: AddOrEditSupportUserViewRefs
) => void;

type _LabelwithInputField9OnChanged = (
  text: boolean,
  d3eState: AddOrEditSupportUserViewRefs
) => void;

export interface AddOrEditSupportUserViewProps extends BaseUIProps {
  key?: string;
  obj: NOKUser;
}
/// To store state data for AddOrEditSupportUserView
class AddOrEditSupportUserViewRefs {
  public cancelButton: CancelButtonState = new CancelButtonState();
  public saveButton: SaveButtonState = new SaveButtonState();
}

interface SaveButtonWithStateProps extends BaseUIProps {
  key?: string;
  d3eState: AddOrEditSupportUserViewRefs;
  _onSaveHandler?: _SaveButtonOnPressed;
}

class SaveButtonState extends ObjectObservable {
  private _disable: boolean = false;
  public _hover: boolean = false;
  public get disable(): boolean {
    return this._disable;
  }
  public setDisable(val: boolean) {
    let isValChanged: boolean = this._disable !== val;

    if (!isValChanged) {
      return;
    }

    this._disable = val;

    this.fire("disable", this);
  }
  public get hover(): boolean {
    return this._hover;
  }
  public setHover(val: boolean) {
    let isValChanged: boolean = this._hover !== val;

    if (!isValChanged) {
      return;
    }

    this._hover = val;

    this.fire("hover", this);
  }
}

class _SaveButtonWithState extends ObservableComponent<SaveButtonWithStateProps> {
  saveButtonFocusNode: ui.FocusNode = new ui.FocusNode();
  static contextType = BuildContext;
  context: React.ContextType<typeof BuildContext>;
  public constructor(props: SaveButtonWithStateProps) {
    super(props);

    this.initState();
  }
  public get saveButton(): SaveButtonState {
    return this.props.d3eState.saveButton;
  }
  public get d3eState(): AddOrEditSupportUserViewRefs {
    return this.props.d3eState;
  }
  public get _onSaveHandler(): _SaveButtonOnPressed {
    return this.props._onSaveHandler;
  }
  public initState() {
    super.initState();

    this.updateObservable("saveButton", null, this.saveButton);

    this.initListeners();

    this.enableBuild = true;
  }
  public initListeners(): void {
    this.on(["saveButton", "saveButton.", "saveButton.hover"], this.rebuild);
  }
  public saveButtonOnEnter(event): void {
    return this.saveButton.setHover(true);
  }
  public saveButtonOnExit(event): void {
    return this.saveButton.setHover(false);
  }
  public dispose(): void {
    this.saveButton.setHover(false);

    super.dispose();
  }
  public render(): ReactNode {
    let cStyle = this.context.theme;

    return ui.Container({
      width: 100,
      child: Button({
        padding: this.saveButton.hover
          ? cStyle.tButtonPrimaryPaddingOnHover
          : cStyle.tButtonPrimaryPaddingOn,
        decoration: this.saveButton.hover
          ? cStyle.tButtonPrimaryDecorationOnHover
          : cStyle.tButtonPrimaryDecorationOn,
        disable: this.saveButton.disable,
        onPressed: () => {
          this._onSaveHandler(this.d3eState);
        },
        onFocusChange: (val) => {},
        child: TextView({ data: "Save" }),
        onEnter: (event) => {
          this.saveButtonOnEnter(event);
        },
        onExit: (event) => {
          this.saveButtonOnExit(event);
        },
      }),
      className: "xd5 hc",
    });
  }
}
function SaveButtonWithState(props: SaveButtonWithStateProps) {
  return React.createElement(_SaveButtonWithState, props);
}

interface CancelButtonWithStateProps extends BaseUIProps {
  key?: string;
  d3eState: AddOrEditSupportUserViewRefs;
  _onCancelHandler?: _CancelButtonOnPressed;
}

class CancelButtonState extends ObjectObservable {
  private _disable: boolean = false;
  public _hover: boolean = false;
  public get disable(): boolean {
    return this._disable;
  }
  public setDisable(val: boolean) {
    let isValChanged: boolean = this._disable !== val;

    if (!isValChanged) {
      return;
    }

    this._disable = val;

    this.fire("disable", this);
  }
  public get hover(): boolean {
    return this._hover;
  }
  public setHover(val: boolean) {
    let isValChanged: boolean = this._hover !== val;

    if (!isValChanged) {
      return;
    }

    this._hover = val;

    this.fire("hover", this);
  }
}

class _CancelButtonWithState extends ObservableComponent<CancelButtonWithStateProps> {
  cancelButtonFocusNode: ui.FocusNode = new ui.FocusNode();
  static contextType = BuildContext;
  context: React.ContextType<typeof BuildContext>;
  public constructor(props: CancelButtonWithStateProps) {
    super(props);

    this.initState();
  }
  public get cancelButton(): CancelButtonState {
    return this.props.d3eState.cancelButton;
  }
  public get d3eState(): AddOrEditSupportUserViewRefs {
    return this.props.d3eState;
  }
  public get _onCancelHandler(): _CancelButtonOnPressed {
    return this.props._onCancelHandler;
  }
  public initState() {
    super.initState();

    this.updateObservable("cancelButton", null, this.cancelButton);

    this.initListeners();

    this.enableBuild = true;
  }
  public initListeners(): void {
    this.on(
      ["cancelButton", "cancelButton.", "cancelButton.hover"],
      this.rebuild
    );
  }
  public cancelButtonOnEnter(event): void {
    return this.cancelButton.setHover(true);
  }
  public cancelButtonOnExit(event): void {
    return this.cancelButton.setHover(false);
  }
  public dispose(): void {
    this.cancelButton.setHover(false);

    super.dispose();
  }
  public render(): ReactNode {
    let cStyle = this.context.theme;

    return ui.Container({
      margin: ui.EdgeInsets.fromLTRB(0.0, 0.0, 10.0, 0.0, new Map()),
      width: 100,
      child: Button({
        padding: this.cancelButton.hover
          ? cStyle.tButtonSecondaryPaddingOnHover
          : cStyle.tButtonSecondaryPaddingOn,
        decoration: this.cancelButton.hover
          ? cStyle.tButtonSecondaryDecorationOnHover
          : cStyle.tButtonSecondaryDecorationOn,
        disable: this.cancelButton.disable,
        onPressed: () => {
          this._onCancelHandler(this.d3eState);
        },
        onFocusChange: (val) => {},
        child: TextView({ data: "Cancel" }),
        onEnter: (event) => {
          this.cancelButtonOnEnter(event);
        },
        onExit: (event) => {
          this.cancelButtonOnExit(event);
        },
      }),
      className: "x32b hc",
    });
  }
}
function CancelButtonWithState(props: CancelButtonWithStateProps) {
  return React.createElement(_CancelButtonWithState, props);
}

class _AddOrEditSupportUserViewState extends ObservableComponent<AddOrEditSupportUserViewProps> {
  static defaultProps = { obj: null };
  d3eState: AddOrEditSupportUserViewRefs = new AddOrEditSupportUserViewRefs();
  errors: Array<string> = ListWrapper.widget(this, "errors");
  private _editInitListeners: Array<Runnable> = [];
  private _d3eModelErrors: Array<string> = [];
  private _nameErrors: Array<string> = [];
  private _emailErrors: Array<string> = [];
  public emailUniqueError: string;
  private _passwordErrors: Array<string> = [];
  private _statusDefaultValue: boolean = true;
  private _userTypeErrors: Array<string> = [];
  private _hospitalErrors: Array<string> = [];
  static contextType = BuildContext;
  context: React.ContextType<typeof BuildContext>;
  public constructor(props: AddOrEditSupportUserViewProps) {
    super(props);

    this.initState();
  }
  public get obj(): NOKUser {
    return this.props.obj;
  }
  public initState() {
    super.initState();

    this.initListeners();

    this.enableBuild = true;
  }
  public initListeners(): void {
    this.updateSyncProperty("obj", this.props.obj);

    this.on(
      [
        "emailErrors",
        "nameErrors",
        "obj",
        "obj.email",
        "obj.mobile",
        "obj.name",
        "obj.password",
        "obj.status",
        "obj.userType",
        "passwordErrors",
      ],
      this.rebuild
    );
  }
  public componentDidUpdate(prevProps: AddOrEditSupportUserViewProps): void {
    super.componentDidUpdate(prevProps);

    if (prevProps.obj !== this.props.obj) {
      this.updateObservable("obj", prevProps.obj, this.props.obj);

      this.fire("obj", this);
    }
  }
  public setErrors(val: Array<string>): void {
    let isValChanged: boolean = CollectionUtils.isNotEquals(this.errors, val);

    if (!isValChanged) {
      return;
    }

    this.errors.clear();

    this.errors.addAll(val);

    this.fire("errors", this);
  }
  public addToErrors(val: string, index: number = -1): void {
    if (index === -1) {
      if (!this.errors.contains(val)) this.errors.add(val);
    } else {
      this.errors.remove(this.errors.elementAt(index));

      this.errors.add(val);
    }

    this.fire("errors", this, val, true);
  }
  public removeFromErrors(val: string): void {
    this.errors.remove(val);

    this.fire("errors", this, val, false);
  }
  public get d3eModelErrors(): Array<string> {
    return this._d3eModelErrors ?? [];
  }
  public set d3eModelErrors(val: Array<string>) {
    this._d3eModelErrors.clear();

    this._d3eModelErrors.addAll(val);
  }
  public get nameErrors(): Array<string> {
    return this._nameErrors ?? [];
  }
  public set nameErrors(val: Array<string>) {
    this._nameErrors.clear();

    this._nameErrors.addAll(val);
  }
  public async computeNameErrors(): Promise<void> {
    this.nameErrors = [];

    let it: string = this.obj.name;

    if (it == null || it === "") {
      this.nameErrors.add("Name cannot be empty.");
    }

    this.fire("nameErrors", this);
  }
  public get emailErrors(): Array<string> {
    return this._emailErrors ?? [];
  }
  public set emailErrors(val: Array<string>) {
    this._emailErrors.clear();

    this._emailErrors.addAll(val);
  }
  public async computeEmailErrors(): Promise<void> {
    this.emailErrors = [];

    let it: string = this.obj.email;

    if (it == null || it === "") {
      this.emailErrors.add("Email cannot be empty.");
    }

    this.fire("emailErrors", this);
  }
  public async checkNOKUserEmailUniqueInHospital(): Promise<void> {
    if (this.obj == null || this.obj.hospital == null) {
      return;
    }

    let isUnique: boolean =
      await RPCServices.getUniqueChecker().checkNOKUserEmailUniqueInHospital(
        this.obj.hospital,
        this.obj,
        this.obj.email
      );

    if (!isUnique) {
      this.emailUniqueError = "Duplicate email found with this value.";
    }
  }
  public get passwordErrors(): Array<string> {
    return this._passwordErrors ?? [];
  }
  public set passwordErrors(val: Array<string>) {
    this._passwordErrors.clear();

    this._passwordErrors.addAll(val);
  }
  public async computePasswordErrors(): Promise<void> {
    this.passwordErrors = [];

    let it: string = this.obj.password;

    if (it == null || it === "") {
      this.passwordErrors.add("Password cannot be empty.");
    }

    this.fire("passwordErrors", this);
  }
  public get userTypeErrors(): Array<string> {
    return this._userTypeErrors ?? [];
  }
  public set userTypeErrors(val: Array<string>) {
    this._userTypeErrors.clear();

    this._userTypeErrors.addAll(val);
  }
  public async computeUserTypeErrors(): Promise<void> {
    this.userTypeErrors = [];

    let it: UserType = this.obj.userType;

    this.fire("userTypeErrors", this);
  }
  public get hospitalErrors(): Array<string> {
    return this._hospitalErrors ?? [];
  }
  public set hospitalErrors(val: Array<string>) {
    this._hospitalErrors.clear();

    this._hospitalErrors.addAll(val);
  }
  public async computeHospitalErrors(): Promise<void> {
    this.hospitalErrors = [];

    let it: Hospital = this.obj.hospital;

    if (it == null) {
      this.hospitalErrors.add("Hospital cannot be null.");
    }

    this.fire("hospitalErrors", this);
  }
  public setEditDefaults(): void {}
  public get isEditValid(): boolean {
    if (this.nameErrors.isNotEmpty) {
      return false;
    }

    if (this.emailErrors.isNotEmpty) {
      return false;
    }

    if (this.passwordErrors.isNotEmpty) {
      return false;
    }

    if (this.userTypeErrors.isNotEmpty) {
      return false;
    }

    if (this.hospitalErrors.isNotEmpty) {
      return false;
    }

    return true;
  }
  public validate(): Array<string> {
    let errors: Array<string> = [];

    this.computeNameErrors();

    errors.addAll(this.nameErrors);

    this.computeEmailErrors();

    errors.addAll(this.emailErrors);

    this.computePasswordErrors();

    errors.addAll(this.passwordErrors);

    this.computeUserTypeErrors();

    errors.addAll(this.userTypeErrors);

    this.computeHospitalErrors();

    errors.addAll(this.hospitalErrors);

    return errors;
  }
  public render(): ReactNode {
    let cStyle = this.context.theme;

    return PopupWrapperView({
      title:
        this.obj.saveStatus !== DBSaveStatus.New
          ? "Edit Support NOK User"
          : "Add Support NOK User",
      content: ui.Column({
        crossAxisAlignment: ui.CrossAxisAlignment.start,
        mainAxisAlignment: ui.MainAxisAlignment.start,
        children: [
          ui.Container({
            width: 400,
            child: LabelWithInputField({
              name: "Name",
              placeHolder: "Enter Name",
              isRequired: true,
              value: this.obj.name,
              errors: this.nameErrors,
              onChanged: (text) => {
                this.labelwithInputFieldonChanged(text, this.d3eState);
              },
            }),
            key: "0",
            className: "x04 hc",
          }),
          ui.Container({
            margin: ui.EdgeInsets.symmetric({
              horizontal: 0.0,
              vertical: 10.0,
              transitions: new Map(),
            }),
            width: 400,
            child: LabelWithInputField({
              name: "Email Address",
              placeHolder: "Enter Email Address",
              isRequired: true,
              value: this.obj.email,
              errors: this.emailErrors,
              onChanged: (text) => {
                this.labelwithInputField2onChanged(text, this.d3eState);
              },
            }),
            key: "1",
            className: "xd7 hc",
          }),
          ui.Container({
            margin: ui.EdgeInsets.symmetric({
              horizontal: 0.0,
              vertical: 10.0,
              transitions: new Map(),
            }),
            width: 400,
            child: LabelWithInputField({
              name: "Password",
              placeHolder: "Enter Password",
              isRequired: true,
              obscureText: true,
              value: this.obj.password,
              errors: this.passwordErrors,
              onChanged: (text) => {
                this.labelwithInputField8onChanged(text, this.d3eState);
              },
            }),
            key: "2",
            className: "xf9 hc",
          }),
          ui.Container({
            margin: ui.EdgeInsets.fromLTRB(0.0, 0.0, 0.0, 5.0, new Map()),
            child: LabelDropdown<UserType>({
              placeHolder: "---Select---",
              name: "Role",
              value: this.obj.userType,
              items: UserType.values,
              isRequired: true,
              width: 400,
              onChanged: (text) => {
                this.labelDropdown1onChanged(text, this.d3eState);
              },
              builder: (context, item) => {
                return TextView({ data: item.name });
              },
            }),
            key: "3",
            className: "x95 hc h",
          }),
          ui.Container({
            margin: ui.EdgeInsets.fromLTRB(0.0, 0.0, 0.0, 10.0, new Map()),
            width: 400,
            child: LabelWithInputField({
              name: "Contact Number",
              placeHolder: "Enter Contact Number",
              isRequired: true,
              value: this.obj.mobile,
              onChanged: (text) => {
                this.labelwithInputField3onChanged(text, this.d3eState);
              },
            }),
            key: "4",
            className: "x27 hc",
          }),
          ui.Container({
            margin: ui.EdgeInsets.fromLTRB(10.0, 0.0, 0.0, 0.0, new Map()),
            child: BooleanToggleView({
              label: "Status",
              activeText: "Active",
              inActiveText: "Inactive",
              value: this.obj.status,
              onChanged: (text) => {
                this.labelwithInputField9onChanged(text, this.d3eState);
              },
            }),
            key: "5",
            className: "x02 hc",
          }),
        ],
        className: "x47 hc h",
      }),
      buttons: [
        ui.Column({
          crossAxisAlignment: ui.CrossAxisAlignment.start,
          mainAxisAlignment: ui.MainAxisAlignment.start,
          children: [
            ui.Row({
              mainAxisAlignment: ui.MainAxisAlignment.start,
              children: [
                CancelButtonWithState({
                  d3eState: this.d3eState,
                  _onCancelHandler: this.onCancelHandler,
                  key: "0",
                }),
                SaveButtonWithState({
                  d3eState: this.d3eState,
                  _onSaveHandler: this.onSaveHandler,
                  key: "1",
                }),
              ],
              key: "0",
            }),
          ],
          key: "0",
        }),
      ],
      className: ui.join(
        this.props.className,
        "AddOrEditSupportUserView hc vc"
      ),
      ...copyBaseUIProps(this.props),
    });
  }
  public onCancelHandler = (d3eState: AddOrEditSupportUserViewRefs): void => {
    this.obj.restore();

    this.navigator.close();
  };
  public onSaveHandler = async (
    d3eState: AddOrEditSupportUserViewRefs
  ): Promise<void> => {
    if (this.validate().isEmpty) {
      let result: Result<NOKUser> = await this.obj.save();

      if (result.status === ResultStatus.Success) {
        EventBus.get().fire(
          new SuccessMessage({ message: "Successfully Saved" })
        );

        this.navigator.close();
      } else {
        this.setErrors(result.errors);
      }
    }
  };
  public labelwithInputFieldonChanged = (
    val: string,
    d3eState: AddOrEditSupportUserViewRefs
  ): void => {
    this.obj.setName(val);
  };
  public labelwithInputField2onChanged = (
    val: string,
    d3eState: AddOrEditSupportUserViewRefs
  ): void => {
    this.obj.setEmail(val);
  };
  public labelwithInputField8onChanged = (
    val: string,
    d3eState: AddOrEditSupportUserViewRefs
  ): void => {
    this.obj.setPassword(val);
  };
  public labelDropdown1onChanged = (
    val: UserType,
    d3eState: AddOrEditSupportUserViewRefs
  ): void => {
    this.obj.setUserType(val);
  };
  public labelwithInputField3onChanged = (
    val: string,
    d3eState: AddOrEditSupportUserViewRefs
  ): void => {
    this.obj.setMobile(val);
  };
  public labelwithInputField9onChanged = (
    val: boolean,
    d3eState: AddOrEditSupportUserViewRefs
  ): void => {
    this.obj.setStatus(val);
  };
  public get navigator(): PageNavigator {
    return PageNavigator.of(this.context);
  }
  public get cancelButton() {
    return this.d3eState.cancelButton;
  }
  public get saveButton() {
    return this.d3eState.saveButton;
  }
}
export default function AddOrEditSupportUserView(
  props: AddOrEditSupportUserViewProps
) {
  return React.createElement(_AddOrEditSupportUserViewState, {
    ..._AddOrEditSupportUserViewState.defaultProps,
    ...props,
  });
}
